/**
 * MCP tool for upgrading the Canary CLI to the latest version.
 *
 * Adapts the `canary upgrade` command for MCP context where
 * stdio is the transport and process.exit() must be avoided.
 *
 * @module
 */

import fs from 'node:fs';
import { spawnSync } from 'node:child_process';
import process from 'node:process';
import { packageName } from '../brand.js';
import { clearUpdateCache } from '../update-check.js';
import { toolJson } from './tool-helpers';

type PackageManager = 'bun' | 'pnpm' | 'npm';

function detectPackageManager(): PackageManager {
  try {
    const binPath = fs.realpathSync(process.argv[1]).replace(/\\/g, '/');
    if (binPath.includes('.bun/') || binPath.includes('/bun/')) return 'bun';
    if (binPath.includes('.pnpm/') || binPath.includes('/pnpm/')) return 'pnpm';
  } catch {
    // realpathSync can fail if symlink is broken
  }
  return 'npm';
}

const UPGRADE_COMMANDS: Record<PackageManager, string[]> = {
  bun: ['bun', 'add', '-g', `${packageName}@latest`],
  pnpm: ['pnpm', 'add', '-g', `${packageName}@latest`],
  npm: ['npm', 'install', '-g', `${packageName}@latest`],
};

export function getUpgradeToolDefinitions() {
  return [
    {
      name: 'canary_upgrade',
      description:
        'Upgrade the Canary CLI to the latest version. ' +
        'Detects the package manager (npm, bun, pnpm) and runs the appropriate global install command.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
  ];
}

export async function handleUpgradeTool(
  tool: string,
  _args: Record<string, unknown>
) {
  if (tool !== 'canary_upgrade') return null;

  try {
    const pm = detectPackageManager();
    const cmd = UPGRADE_COMMANDS[pm];

    const result = spawnSync(cmd[0], cmd.slice(1), {
      stdio: ['pipe', 'pipe', 'pipe'],
      cwd: process.cwd(),
      timeout: 120_000,
      shell: process.platform === 'win32',
      windowsHide: true,
    });

    if (result.error) {
      return toolJson({
        ok: false,
        error: `Failed to run ${pm}: ${result.error.message}`,
      });
    }

    const stdout = result.stdout?.toString().trim() ?? '';
    const stderr = result.stderr?.toString().trim() ?? '';

    if (result.status !== 0) {
      return toolJson({
        ok: false,
        error: `Upgrade failed (exit code ${result.status}).`,
        output: stderr || stdout,
      });
    }

    await clearUpdateCache();

    return toolJson({
      ok: true,
      message: `Successfully upgraded ${packageName} to the latest version.`,
      packageManager: pm,
      output: stdout || undefined,
    });
  } catch (err) {
    return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
  }
}
